import { FC, useCallback } from 'react';
import { Box, TableCell } from '@mui/material';
import ActionToolTip from '../../../components/formlib/ActionToolTip';
import { ReactComponent as ProgressNote } from '../../../assets/images/ProgressNoteIcon.svg';
import { ReactComponent as MessageIcon } from '../../../assets/images/messageIcon.svg';
import { ReactComponent as UndoIcon } from '../../../assets/images/undoIcon.svg';
import { ReactComponent as OnHoldIcon } from '../../../assets/images/onHoldIcon.svg';
import { Appointment } from '../../../services/configApi/activitySheet/weeklyActivitySheetProvider';
import { checkPermissionForFeature } from '../../../utils/checkPermission';
import { getStatusColor } from '../../../utils/utilities';
import { PROGRESS_NOTE_SETUP } from '../../../routes/Routing';
import { StatusType } from '../ActivityTableRow';
import { VisitNote } from '../ActivitySheetVisitView';
import { ActivitySheetContextType } from '../../../context/ActivitySheetContext';
import { NavigateFunction } from 'react-router-dom';
import { IntlShape } from 'react-intl';

interface VisitActionButtonsProps {
  appointment: Appointment;
  removeBorder: boolean;
  parentStatus: StatusType | null;
  selectedDate?: Date;
  activitySheetId: string;
  activitySheetContext: ActivitySheetContextType | null;
  onUpdateVisitNote: (note: VisitNote) => void;
  navigate: NavigateFunction;
  rctl: IntlShape;
}

const VisitActionButtons: FC<VisitActionButtonsProps> = ({
  appointment,
  removeBorder,
  parentStatus,
  selectedDate,
  activitySheetId,
  activitySheetContext,
  onUpdateVisitNote,
  navigate,
  rctl,
}) => {
  const handleProgressNoteClick = useCallback(() => {
    const attendies: { id: string }[] = [];
    const clientIds: string[] = [];

    attendies.push({ id: appointment.attendeeId });
    clientIds.push(appointment.clientId);

    if (appointment.commonAppointmentId) {
      navigate(PROGRESS_NOTE_SETUP, {
        state: {
          sortRequired: false,
          eventId: appointment.commonAppointmentId,
          attendeeId: attendies,
          clientIds: clientIds,
          screenFrom: 'ApprovalActivitySheet',
          selectedDate: selectedDate,
        },
      });
    }
  }, [appointment, navigate, parentStatus, selectedDate]);

  const handleNoteClick = useCallback(() => {
    if (appointment.visitId) {
      onUpdateVisitNote({
        progressNotesId: appointment.progressNotesId,
        visitId: appointment.visitId,
        note: appointment.description ?? '',
      });
    }
  }, [appointment, onUpdateVisitNote]);

  const handleRevertClick = useCallback(() => {
    if (
      activitySheetContext?.updateVisitStatus &&
      appointment.visitId &&
      checkPermissionForFeature('backend.billing_hours_ras', 'editPermission')
    ) {
      activitySheetContext?.updateVisitStatus?.(
        activitySheetId,
        appointment.visitId,
        'Submitted',
        false,
      );
    }
  }, [activitySheetContext, activitySheetId, appointment.visitId]);

  const handleOnHoldClick = useCallback(() => {
    if (
      activitySheetContext?.updateVisitOnHold &&
      appointment.visitId &&
      checkPermissionForFeature(
        'backend.on_hold_progress_notes_visits',
        'editPermission',
      )
    ) {
      const onHold = appointment.onHold ?? false;
      activitySheetContext.updateVisitOnHold(
        activitySheetId,
        appointment.visitId,
        !onHold,
      );
    }
  }, [activitySheetContext, activitySheetId, appointment.visitId]);

  const cellClassName = removeBorder ? 'border-bottom-0' : 'enableBorderBottom';
  const canShowProgressNote = checkPermissionForFeature(
    'backend.progress_note',
    'viewPermission',
  );
  const isActivityRejected =
    activitySheetContext?.activityStatus === 'Rejected';
  const canShowRevert =
    appointment.originalStatus === 'Approved' &&
    appointment.status === 'Approved' &&
    !isActivityRejected &&
    checkPermissionForFeature('backend.billing_hours_ras', 'editPermission');
  const canShowOnHold =
    appointment.visitId !== null &&
    appointment.visitId !== '' &&
    !appointment.onHold &&
    appointment.status !== 'Approved' &&
    appointment.attendeeRole !== 'INTERPRETER' &&
    appointment.attendeeRole !== 'GROUP_ASSISTANT' &&
    appointment.originalStatus !== null &&
    appointment.originalStatus !== 'Approved' &&
    checkPermissionForFeature(
      'backend.on_hold_progress_notes_visits',
      'editPermission',
    );

  return (
    <TableCell sx={{ paddingLeft: '4px' }} className={cellClassName}>
      <Box className="alignItemCenter" sx={{ display: 'flex' }}>
        {canShowProgressNote && (
          <ActionToolTip
            label={`${rctl.formatMessage({
              id: 'progressNoteText',
              defaultMessage: 'Progress Note',
            })}: ${appointment.progressNotesStatus}`}
          >
            <Box
              sx={{
                display: 'flex',
                visibility:
                  appointment.attendeeRole !== 'CLINICIAN'
                    ? 'hidden'
                    : 'visible',
                cursor: 'pointer',
              }}
              onClick={handleProgressNoteClick}
            >
              <ProgressNote
                stroke={getStatusColor(appointment.progressNotesStatus)}
              />
            </Box>
          </ActionToolTip>
        )}
        {appointment.visitId && (
          <ActionToolTip labelId="BlockTime.note" defaultLabel="Note">
            <Box
              sx={{
                padding: '4px',
                display: 'flex',
                cursor: 'pointer',
                marginLeft: '8px',
              }}
              onClick={handleNoteClick}
            >
              <MessageIcon
                stroke={
                  appointment.description && appointment.description.length > 0
                    ? '#00C6B8'
                    : '#97A6A5'
                }
              />
            </Box>
          </ActionToolTip>
        )}

        <ActionToolTip labelId="onHoldText" defaultLabel="On-hold">
          <Box
            sx={{
              display: 'flex',
              cursor: 'pointer',
              marginLeft: '8px',
              visibility: canShowOnHold ? 'visible' : 'hidden',
            }}
            onClick={handleOnHoldClick}
          >
            {!appointment.onHold && <OnHoldIcon />}
          </Box>
        </ActionToolTip>

        {canShowRevert && !appointment.onHold && (
          <ActionToolTip labelId="revertText" defaultLabel="Revert">
            <Box
              sx={{
                display: 'flex',
                pointerEvents: isActivityRejected ? 'none' : 'auto',
                cursor: isActivityRejected ? 'none' : 'pointer',
                marginLeft: '8px',
              }}
              onClick={handleRevertClick}
            >
              <UndoIcon stroke={'var(--thom-radical-red)'} />
            </Box>
          </ActionToolTip>
        )}
      </Box>
    </TableCell>
  );
};

export default VisitActionButtons;
